import { Search } from '@mui/icons-material';
import { IconButton, InputAdornment } from '@mui/material';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCanViewOrders } from '../../../shared/utils/permissions';

type Props = {
  /** Set by `styled(OrderSearch)`. */
  className?: string;
  /** Take the caret on mount — the mobile bar opens this field in answer to a tap, so it should be ready to type in. */
  autoFocus?: boolean;
  /** A search was sent. The mobile bar uses it to collapse itself again. */
  onSubmitted?: () => void;
  /** Escape pressed with the field focused. Lets the mobile bar close the way a dialog would. */
  onDismiss?: () => void;
};

const OrderSearch: React.FC<Props> = ({ className, autoFocus, onSubmitted, onDismiss }) => {
  const [query, setQuery] = useState('');

  const navigate = useNavigate();
  const { t } = useTranslation();
  const canViewOrders = useCanViewOrders();

  // Encoded, so that an address with a `+` in it survives the trip through the URL rather than
  // arriving as a space the search then fails to match.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/orders?q=${encodeURIComponent(query)}`);
    onSubmitted?.();
  };

  if (!canViewOrders) return null;

  // A form, since a phone keyboard's search key submits forms but isn't reliably reported as Enter.
  return (
    <form className={className} role="search" onSubmit={handleSubmit}>
      <TextField
        autoFocus={autoFocus}
        placeholder={t('Order id, email')}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onDismiss?.();
          }
        }}
        variant="outlined"
        size="small"
        inputProps={{ enterKeyHint: 'search' }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton type="submit" aria-label={t('global.search')}>
                <Search />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </form>
  );
};

export default OrderSearch;
